import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHeartbeat, FaCamera, FaMicrophone, FaWifi, FaBatteryFull } from 'react-icons/fa';

const LoadingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  overflow: hidden;
`;

const BackgroundParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Particle = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
`;

const LogoContainer = styled(motion.div)`
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
`;

const LogoIcon = styled(motion.div)`
  font-size: 48px;
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 10px;
  text-align: center;
  background: linear-gradient(135deg, #ffffff, #e0e7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(motion.p)`
  font-size: 18px;
  opacity: 0.8;
  margin-bottom: 40px;
  text-align: center;
`;

const StatusContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
`;

const StatusItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 250px;
`;

const StatusIcon = styled.div`
  font-size: 20px;
  color: ${props => props.status ? '#10b981' : '#f59e0b'};
`;

const StatusText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const ProgressBar = styled(motion.div)`
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 3px;
`;

const LoadingText = styled(motion.div)`
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`;

const LoadingScreen = () => {
  const [progress, setProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState(0);
  const [statuses, setStatuses] = useState([
    { id: 'camera', text: 'Vérification de la caméra...', icon: FaCamera, status: false },
    { id: 'microphone', text: 'Vérification du microphone...', icon: FaMicrophone, status: false },
    { id: 'battery', text: 'Vérification de la batterie...', icon: FaBatteryFull, status: false },
    { id: 'internet', text: 'Vérification de la connexion...', icon: FaWifi, status: false },
    { id: 'system', text: 'Initialisation du système...', icon: FaHeartbeat, status: false },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const statusInterval = setInterval(() => {
      setCurrentStatus(prev => {
        if (prev >= statuses.length - 1) {
          clearInterval(statusInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(statusInterval);
  }, [statuses.length]);

  useEffect(() => {
    setStatuses(prev => 
      prev.map((status, index) => ({
        ...status,
        status: index <= currentStatus
      }))
    );
  }, [currentStatus]);

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
  }));

  return (
    <LoadingContainer>
      <BackgroundParticles>
        {particles.map((particle) => (
          <Particle
            key={particle.id}
            initial={{ opacity: 0, y: 100 }}
            animate={{ 
              opacity: [0, 1, 0], 
              y: [100, -100],
              x: [particle.x, particle.x + Math.random() * 50 - 25]
            }}
            transition={{
              duration: 3,
              delay: particle.delay,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
            }}
          />
        ))}
      </BackgroundParticles>

      <LogoContainer
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <LogoIcon
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          ❤️
        </LogoIcon>
      </LogoContainer>

      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        Exam Video Recorder
      </Title>

      <Subtitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        Dispositif médical innovant pour l'évaluation des AVC
      </Subtitle>

      <StatusContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.7 }}
      >
        {statuses.map((status, index) => (
          <StatusItem
            key={status.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 + index * 0.2 }}
          >
            <StatusIcon status={status.status}>
              <status.icon />
            </StatusIcon>
            <StatusText>{status.text}</StatusText>
          </StatusItem>
        ))}
      </StatusContainer>

      <ProgressBar
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.8, delay: 1.5 }}
      >
        <ProgressFill
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress / 100 }}
          transition={{ duration: 0.3 }}
        />
      </ProgressBar>

      <LoadingText
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.7 }}
      >
        {progress < 100 ? `Chargement... ${progress}%` : 'Prêt !'}
      </LoadingText>
    </LoadingContainer>
  );
};

export default LoadingScreen; 